/**
 * Client REST pour l'API boost-core /fiber-links (monitoring liens fibre via Thanos).
 * Utilise apiFetch (auth cookie, CSRF sur mutations, recovery 401).
 * Miroir de apps/boostcore/src/thanos-monitoring/.
 */
import { apiFetch } from '@/auth/apiFetch'
import type {
  FiberLink,
  FiberLinkAvailability,
  FiberLinkEventsResponse,
  FiberLinkSearchHit,
  LinkRange,
  StateHistory,
  Throughput,
} from '@/types/fiberMonitoring'

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    throw new Error(`${res.status} ${res.statusText}${body ? ` — ${body}` : ''}`)
  }
  return (await res.json()) as T
}

export function getFiberLink(codeContrat: string): Promise<FiberLink> {
  return apiFetch(`/fiber-links/${encodeURIComponent(codeContrat)}`).then(json<FiberLink>)
}

export function getStatusHistory(codeContrat: string, range: LinkRange): Promise<StateHistory> {
  return apiFetch(
    `/fiber-links/${encodeURIComponent(codeContrat)}/status-history?range=${range}`,
  ).then(json<StateHistory>)
}

export function getThroughput(codeContrat: string, range: LinkRange): Promise<Throughput> {
  return apiFetch(
    `/fiber-links/${encodeURIComponent(codeContrat)}/throughput?range=${range}`,
  ).then(json<Throughput>)
}

export function getStateEvents(
  codeContrat: string,
  range: LinkRange,
): Promise<FiberLinkEventsResponse> {
  return apiFetch(
    `/fiber-links/${encodeURIComponent(codeContrat)}/state-events?range=${range}`,
  ).then(json<FiberLinkEventsResponse>)
}

export interface SyncFiberLinkResult {
  matched: number
  eventsInserted: number
  days: number
}

export function syncFiberLink(
  codeContrat: string,
  days = 30,
): Promise<SyncFiberLinkResult> {
  return apiFetch(
    `/fiber-links/${encodeURIComponent(codeContrat)}/sync?days=${days}`,
    { method: 'POST' },
  ).then(json<SyncFiberLinkResult>)
}

export function searchFiberLinks(q: string, limit = 20): Promise<FiberLinkSearchHit[]> {
  const params = new URLSearchParams()
  if (q) params.set('q', q)
  params.set('limit', String(limit))
  return apiFetch(`/fiber-links/search?${params.toString()}`).then(json<FiberLinkSearchHit[]>)
}

export function getAvailability(
  codeContrat: string,
  range: LinkRange,
): Promise<FiberLinkAvailability> {
  return apiFetch(
    `/fiber-links/${encodeURIComponent(codeContrat)}/availability?range=${range}`,
  ).then(json<FiberLinkAvailability>)
}
